"use client";

import React, { useState, useMemo, useEffect, useCallback } from "react";
import ProductConfigPanel from "./ProductConfigPanel";
import ProductContent from "./ProductContent";
import ProductConfigResult from "./ProductConfigResult";
import ProductDescription from "./ProductDescription";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import { useLocale } from "next-intl";

interface ProductPageClientProps {
	product: any;
}

export default function ProductPageClient({ product }: ProductPageClientProps) {
	const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
	const { setVariantActive3DName } = useModalInquiryContext();
	const locale = useLocale();

	// 获取属性组列表
	const attributeGroups = useMemo(() => {
		if (!product?.variants || product.variants.length === 0) {
			return [];
		}

		const attributeMap = new Map<string, Set<string>>();

		// 方法1: 从 product.attributes 获取属性定义
		if (product.attributes && Array.isArray(product.attributes)) {
			product.attributes.forEach((attr: any) => {
				const attrName = attr.attribute?.translation?.name || attr.attribute?.name || "";
				if (attrName && attr.values && Array.isArray(attr.values)) {
					if (!attributeMap.has(attrName)) {
						attributeMap.set(attrName, new Set());
					}
					attr.values.forEach((value: any) => {
						const valueName = value.translation?.name || value.name || "";
						if (valueName) {
							attributeMap.get(attrName)!.add(valueName);
						}
					});
				}
			});
		}

		// 方法2: 如果方法1没有结果，从 variants 中提取
		if (attributeMap.size === 0) {
			product.variants.forEach((variant: any) => {
				if (variant.attributes && Array.isArray(variant.attributes)) {
					variant.attributes.forEach((attr: any) => {
						const attrName = attr.attribute?.translation?.name || attr.attribute?.name || "";
						const attrValue = attr.values?.[0]?.translation?.name || attr.values?.[0]?.name || "";
						
						if (attrName && attrValue) {
							if (!attributeMap.has(attrName)) {
								attributeMap.set(attrName, new Set());
							}
							attributeMap.get(attrName)!.add(attrValue);
						}
					});
				}
			});
		}

		// 转换为数组格式，并过滤掉没有值的属性组
		return Array.from(attributeMap.entries())
			.map(([name, values]) => ({
				name,
				values: Array.from(values).sort(),
			}))
			.filter(group => group.name && group.values.length > 0); // 过滤掉空名称或空值的属性组
	}, [product?.variants, product?.attributes, locale]);

	// 检查是否有属性可配置
	const hasAttributes = useMemo(() => {
		return attributeGroups.length > 0 && attributeGroups.some(group => group.values.length > 0);
	}, [attributeGroups]);

	// 初始化默认选中第一个 variant 的属性
	useEffect(() => {
		if (product?.variants && product.variants.length > 0 && Object.keys(selectedAttributes).length === 0) {
			const firstVariant = product.variants[0];
			if (firstVariant?.attributes && Array.isArray(firstVariant.attributes)) {
				const initialAttributes: Record<string, string> = {};
				firstVariant.attributes.forEach((attr: any) => {
					const name = attr.attribute?.translation?.name || attr.attribute?.name || "";
					const value = attr.values?.[0]?.translation?.name || attr.values?.[0]?.name || "";
					if (name && value) {
						initialAttributes[name] = value;
					}
				});
				setSelectedAttributes(initialAttributes);
				setVariantActive3DName(firstVariant.name);
			}
		}
	}, [product?.variants]);

	// 根据选中的属性找到匹配的 variant（实时更新）
	const matchedVariant = useMemo(() => {
		if (!product?.variants || product.variants.length === 0) return null;
		
		// 如果没有选中任何属性，返回第一个 variant
		if (Object.keys(selectedAttributes).length === 0) {
			return product.variants[0];
		}

		// 找到所有属性都匹配的 variant
		const matched = product.variants.find((variant: any) => {
			if (!variant.attributes || !Array.isArray(variant.attributes)) return false;
			
			// 检查所有选中的属性是否都在这个 variant 中，并且值匹配
			const allMatch = Object.entries(selectedAttributes).every(([attrName, attrValue]) => {
				const variantAttr = variant.attributes.find((attr: any) => {
					const name = attr.attribute?.translation?.name || attr.attribute?.name || "";
					return name === attrName;
				});
				
				if (!variantAttr || !variantAttr.values || variantAttr.values.length === 0) return false;
				
				const value = variantAttr.values[0]?.translation?.name || variantAttr.values[0]?.name || "";
				return value === attrValue;
			});

			// 同时检查 variant 的所有属性是否都在 selectedAttributes 中（避免部分匹配）
			if (allMatch) {
				const variantAttrNames = variant.attributes.map((attr: any) => {
					return attr.attribute?.translation?.name || attr.attribute?.name || "";
				}).filter(Boolean);
				
				// 确保选中的属性数量与 variant 的属性数量一致
				return variantAttrNames.length === Object.keys(selectedAttributes).length;
			}
			
			return false;
		});

		return matched || product.variants[0];
	}, [product?.variants, selectedAttributes]);

	// 处理属性变化（实时更新）
	const handleAttributeChange = useCallback((attributeName: string, value: string) => {
		setSelectedAttributes((prev) => {
			const updated = {
				...prev,
				[attributeName]: value,
			};
			return updated;
		});
	}, []);

	// 当匹配的 variant 变化时，更新 3D 模型名称
	useEffect(() => {
		if (matchedVariant) {
			setVariantActive3DName(matchedVariant.name);
		}
	}, [matchedVariant, setVariantActive3DName]);

	// 创建包含选中 variant 的产品对象
	const productWithVariant = useMemo(() => {
		if (!matchedVariant) return product;
		return {
			...product,
			selectedVariant: matchedVariant,
		};
	}, [product, matchedVariant]);

	return (
		<div className="w-full">
			<div className={`grid gap-8 max-lg:grid-cols-1 ${hasAttributes ? 'grid-cols-9' : 'grid-cols-1'}`}>
				{/* 左侧：配置面板 - 只在有属性时显示 */}
				{hasAttributes && (
					<div className="col-span-2 max-lg:col-span-1 max-lg:order-2 product-sticky-sidebar">
						<ProductConfigPanel 
							product={product} 
							onAttributeChange={handleAttributeChange}
							selectedAttributes={selectedAttributes}
						/>
					</div>
				)}

				{/* 右侧：产品内容和配置结果 */}
				<div className={hasAttributes ? "col-span-7 max-lg:col-span-1 max-lg:order-1" : "col-span-1"}>
					<ProductContent product={productWithVariant} />
					
					{/* 配置结果显示 - 只在有属性时显示 */}
					{hasAttributes && (
						<div className="mt-6">
							<ProductConfigResult
								product={product}
								selectedAttributes={selectedAttributes}
								matchedVariant={matchedVariant}
								attributeGroups={attributeGroups}
							/>
						</div>
					)}
					
					{/* 产品描述 - 放在右侧底部 */}
					<div className="mt-10">
						<ProductDescription product={product} locale={locale} />
					</div>
				</div>
			</div>
		</div>
	);
}

